import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Upload, X, Package, ClipboardList, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetOrders, adminUpdateOrderStatus, adminCreateProduct, adminUpdateProduct,
  adminDeleteProduct, adminUploadImage, adminGetSettings, adminUpdateSettings,
  getProducts, inr, imgSrc, LOGO_URL,
  stockGetOrders, stockGetProducts, stockUpdateOrderStatus, stockUpdateProductStock,
} from "@/api";

const ORDER_STATUSES = ["placed", "confirmed", "dispatched", "delivered", "cancelled"];
const EMPTY_PRODUCT = {
  name: "", category: "short-kurta", price: 0, description: "",
  fabric: "", images: [], badge: "", featured: false,
  stock: { XS: 6, S: 8, M: 10, L: 10, XL: 8, XXL: 6 },
};

const SIZE_KEYS = ["XS", "S", "M", "L", "XL", "XXL"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [stockDrafts, setStockDrafts] = useState({});
  const isStockManager = localStorage.getItem("vaiyra_admin_role") === "stock_manager";

  const load = useCallback(async () => {
    try {
      if (isStockManager) {
        const [o, p] = await Promise.all([stockGetOrders(), stockGetProducts()]);
        setOrders(o);
        setProducts(p);
        setStockDrafts(Object.fromEntries(p.map((product) => [product.id, { ...(product.stock || {}) }])));
        setSettings(null);
      } else {
        const [o, p, s] = await Promise.all([adminGetOrders(), getProducts(), adminGetSettings()]);
        setOrders(o);
        setProducts(p);
        setSettings(s);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("vaiyra_admin_token");
        navigate("/admin/login");
      }
    }
  }, [isStockManager, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("vaiyra_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("vaiyra_admin_token");
    localStorage.removeItem("vaiyra_admin_role");
    navigate("/admin/login");
  };

  const handleStatus = async (orderId, status) => {
    if (isStockManager) {
      await stockUpdateOrderStatus(orderId, status);
    } else {
      await adminUpdateOrderStatus(orderId, status);
    }
    setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, status } : o)));
    toast.success(`Order marked ${status}`);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const saved = await adminUpdateSettings({
        ...settings,
        shipping_fee: Math.max(0, Number(settings.shipping_fee) || 0),
        free_shipping_above: Math.max(0, Number(settings.free_shipping_above) || 0),
      });
      setSettings(saved);
      toast.success("Store settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const res = await adminUploadImage(f);
        setEditing((prev) => ({ ...prev, images: [...prev.images, res.url] }));
      }
      toast.success("Photograph added to the piece");
    } catch {
      toast.error("Upload failed — try a JPG/PNG/WebP under 10 MB");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSaveProduct = async () => {
    if (!editing.name || !editing.price || editing.images.length === 0) {
      toast.error("Name, price and at least one photograph are required");
      return;
    }
    setSaving(true);
    const payload = { ...editing, price: Number(editing.price), badge: editing.badge || null };
    try {
      if (editing.id) {
        await adminUpdateProduct(editing.id, payload);
        toast.success(`${editing.name} updated`);
      } else {
        await adminCreateProduct(payload);
        toast.success(`${editing.name} added to the collection`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the piece");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStock = async (product) => {
    try {
      const updated = await stockUpdateProductStock(product.id, stockDrafts[product.id] || {});
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
      setStockDrafts((prev) => ({ ...prev, [product.id]: { ...(updated.stock || {}) } }));
      toast.success(`${product.name} stock updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update stock");
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the collection?`)) return;
    await adminDeleteProduct(p.id);
    toast.success(`${p.name} removed`);
    load();
  };

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-20 px-6 sm:px-12 max-w-7xl mx-auto min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Vaiyra" className="w-10 h-10 rounded-full ring-1 ring-gold/50 object-cover" />
          <div>
            <h1 className="font-display text-3xl text-maroon-deep">{isStockManager ? "Stock Room" : "The Atelier"}</h1>
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold-dark">{isStockManager ? "Vaiyra Inventory & Orders" : "Vaiyra Back Office"}</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-clay hover:text-maroon border border-maroon/20 px-4 py-2 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Leave
        </button>
      </div>

      <div className="mt-8 flex gap-2 border-b border-gold/25 pb-0">
        {[
          { key: "orders", label: "Orders", icon: ClipboardList },
          { key: "products", label: isStockManager ? "Stock Room" : "The Collection", icon: Package },
          ...(!isStockManager ? [{ key: "settings", label: "Store Settings", icon: Settings2 }] : []),
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => { setTab(t.key); setEditing(null); }}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.25em] uppercase border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-maroon text-maroon-deep" : "border-transparent text-clay hover:text-maroon-deep"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div data-testid="admin-orders-tab" className="mt-8 space-y-4">
          {orders.length === 0 && <p className="font-display italic text-xl text-maroon-deep/60 py-16 text-center">No orders yet — the first one is on its way.</p>}
          {orders.map((o) => (
            <div key={o._id} data-testid={`admin-order-${o.order_number}`} className="bg-ivory-card border border-gold/25 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-brand tracking-[0.15em] text-maroon-deep">{o.order_number}</p>
                  <p className="text-sm text-clay mt-1">
                    {o.customer.name} · {o.customer.city}, {o.customer.state} · {o.customer.phone}
                  </p>
                  <p className="text-xs text-clay/70 mt-1">{o.customer.email}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-maroon-deep">{inr(o.total)}</p>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-gold-dark mt-1">
                    {o.payment_method === "cod" ? "Cash on Delivery" : "Online"} · {o.payment_status}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-gold/20 pt-4">
                <p className="text-sm text-clay">
                  {o.items.map((i) => `${i.name} (${i.size}×${i.qty})`).join(", ")}
                </p>
                <select
                  data-testid={`admin-order-status-${o.order_number}`}
                  value={o.status}
                  onChange={(e) => handleStatus(o._id, e.target.value)}
                  className="bg-ivory border border-maroon/20 px-3 py-2 text-xs tracking-[0.2em] uppercase text-maroon-deep focus:outline-none focus:border-gold"
                >
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && isStockManager && (
        <div data-testid="stock-manager-products-tab" className="mt-8 space-y-5">
          <div className="bg-ivory-card border border-gold/25 p-5">
            <h2 className="font-display text-2xl text-maroon-deep">Inventory</h2>
            <p className="mt-1 text-sm text-clay">Update the physical stock count for each size as soon as new pieces arrive.</p>
          </div>
          {products.map((p) => (
            <div key={p.id} className="bg-ivory-card border border-gold/25 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-gold-dark">{p.category_label}</p>
                  <h3 className="mt-1 font-display text-2xl text-maroon-deep">{p.name}</h3>
                  <p className="mt-1 text-xs text-clay">Current total: {Object.values(p.stock || {}).reduce((a, b) => a + Number(b || 0), 0)} pieces</p>
                </div>
                <button
                  onClick={() => handleSaveStock(p)}
                  className="bg-maroon text-ivory px-5 py-3 text-xs tracking-[0.25em] uppercase hover:bg-maroon-hover transition-colors"
                >Save Stock
                </button>
              </div>
              <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-3">
                {SIZE_KEYS.map((s) => (
                  <label key={s} className="block">
                    <span className="text-[10px] tracking-[0.2em] text-clay">{s}</span>
                    <input
                      type="number"
                      min="0"
                      value={stockDrafts[p.id]?.[s] ?? 0}
                      onChange={(e) => setStockDrafts((prev) => ({
                        ...prev,
                        [p.id]: { ...(prev[p.id] || {}), [s]: Math.max(0, Number(e.target.value) || 0) },
                      }))}
                      className="mt-1 w-full bg-ivory border border-maroon/20 px-3 py-2.5 text-sm text-maroon-deep focus:outline-none focus:border-gold"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && !isStockManager && !editing && (
        <div data-testid="admin-products-tab" className="mt-8">
          <button
            data-testid="admin-add-product-button"
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            className="inline-flex items-center gap-2 bg-maroon text-ivory px-6 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-maroon-hover transition-colors"
          >
            <Plus className="w-4 h-4" /> Add a New Piece
          </button>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-ivory-card border border-gold/25 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-ivory-deep">
                  <img src={imgSrc(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-gold-dark">{p.category_label}{p.featured ? " · Featured" : ""}</p>
                  <div className="flex items-baseline justify-between mt-1">
                    <p className="font-display text-xl text-maroon-deep">{p.name}</p>
                    <p className="font-display text-lg text-maroon-deep">{inr(p.price)}</p>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      data-testid={`admin-edit-${p.id}`}
                      onClick={() => setEditing({ ...p, badge: p.badge || "" })}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-maroon/25 px-3 py-2.5 text-[11px] tracking-[0.2em] uppercase text-maroon-deep hover:border-gold transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      data-testid={`admin-delete-${p.id}`}
                      onClick={() => handleDelete(p)}
                      className="inline-flex items-center justify-center border border-maroon/25 px-3 py-2.5 text-maroon hover:bg-maroon hover:text-ivory transition-colors"
                      aria-label={`Delete ${p.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "products" && !isStockManager && editing && (
        <div data-testid="admin-product-form" className="mt-8 max-w-2xl bg-ivory-card border border-gold/30 p-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-maroon-deep">{editing.id ? `Edit · ${editing.name}` : "A New Piece"}</h2>
            <button data-testid="admin-form-cancel" onClick={() => setEditing(null)} className="text-clay hover:text-maroon" aria-label="Cancel">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <label className="block sm:col-span-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Name</span>
              <input data-testid="admin-form-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" placeholder="Meher Long Kurta" />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Category</span>
              <select data-testid="admin-form-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold">
                <option value="short-kurta">Short Kurta</option>
                <option value="long-kurta">Long Kurta</option>
                <option value="cord-set">Cord Set</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Price (₹)</span>
              <input data-testid="admin-form-price" type="number" min="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" placeholder="2490" />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Badge (optional)</span>
              <input data-testid="admin-form-badge" value={editing.badge} onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" placeholder="New / Bestseller / Signature" />
            </label>
            <label className="flex items-center gap-3 pt-6">
              <input data-testid="admin-form-featured" type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                className="accent-[#5C1A1B] w-4 h-4" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Feature on homepage</span>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Description</span>
              <textarea data-testid="admin-form-description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" placeholder="The story of this piece…" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Fabric & Care</span>
              <input data-testid="admin-form-fabric" value={editing.fabric} onChange={(e) => setEditing({ ...editing, fabric: e.target.value })}
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" placeholder="Mulberry silk · Dry clean only" />
            </label>
            <div className="sm:col-span-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Stock per Size</span>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-3">
                {SIZE_KEYS.map((s) => (
                  <label key={s} className="block">
                    <span className="text-[10px] tracking-[0.2em] text-clay">{s}</span>
                    <input
                      data-testid={`admin-form-stock-${s}`}
                      type="number"
                      min="0"
                      value={editing.stock?.[s] ?? 0}
                      onChange={(e) => setEditing({ ...editing, stock: { ...(editing.stock || {}), [s]: Math.max(0, Number(e.target.value) || 0) } })}
                      className="mt-1 w-full bg-ivory border border-maroon/20 px-3 py-2.5 text-sm text-maroon-deep focus:outline-none focus:border-gold"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Photographs</span>
            <div className="mt-3 flex flex-wrap gap-4">
              {editing.images.map((url, i) => (
                <div key={url} data-testid={`admin-form-image-${i}`} className="relative w-24 h-32 ring-1 ring-gold/40 overflow-hidden group">
                  <img src={imgSrc(url)} alt={`Piece view ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    data-testid={`admin-form-image-remove-${i}`}
                    onClick={() => setEditing({ ...editing, images: editing.images.filter((_, idx) => idx !== i) })}
                    className="absolute top-1 right-1 bg-maroon-ink/70 text-ivory p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label
                data-testid="admin-form-upload"
                className="w-24 h-32 border border-dashed border-gold/60 flex flex-col items-center justify-center gap-2 cursor-pointer text-gold-dark hover:bg-ivory-deep transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span className="text-[9px] tracking-[0.2em] uppercase">{uploading ? "Weaving…" : "Upload"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>
          </div>

          <button
            data-testid="admin-form-save"
            onClick={handleSaveProduct}
            disabled={saving || uploading}
            className="mt-8 w-full bg-maroon text-ivory py-4 text-xs tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : editing.id ? "Save Changes" : "Add to the Collection"}
          </button>
        </div>
      )}

      {!isStockManager && tab === "settings" && settings && (
        <div data-testid="admin-settings-tab" className="mt-8 max-w-2xl bg-ivory-card border border-gold/30 p-8">
          <h2 className="font-display text-2xl text-maroon-deep">Store Settings</h2>
          <p className="mt-2 text-sm text-clay">Every dial of the maison, in one place.</p>

          <div className="mt-8 space-y-6">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark border-b border-gold/25 pb-2">Shipping</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Shipping Fee (₹ · 0 = always free)</span>
                <input data-testid="settings-shipping-fee" type="number" min="0" value={settings.shipping_fee}
                  onChange={(e) => setSettings({ ...settings, shipping_fee: e.target.value })}
                  className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Free Above (₹ · 0 = no threshold)</span>
                <input data-testid="settings-free-above" type="number" min="0" value={settings.free_shipping_above}
                  onChange={(e) => setSettings({ ...settings, free_shipping_above: e.target.value })}
                  className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep focus:outline-none focus:border-gold" />
              </label>
            </div>

            <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark border-b border-gold/25 pb-2 pt-4">Payments</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="settings-cod-toggle" type="checkbox" checked={settings.cod_enabled}
                onChange={(e) => setSettings({ ...settings, cod_enabled: e.target.checked })} className="accent-[#5C1A1B] w-4 h-4" />
              <span className="text-sm text-maroon-deep">Cash on Delivery available at checkout</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="settings-stripe-toggle" type="checkbox" checked={settings.stripe_enabled}
                onChange={(e) => setSettings({ ...settings, stripe_enabled: e.target.checked })} className="accent-[#5C1A1B] w-4 h-4" />
              <span className="text-sm text-maroon-deep">Online card payment (Stripe) available at checkout</span>
            </label>

            <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark border-b border-gold/25 pb-2 pt-4">Low-Stock Alerts</p>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Alert Email · "only X left" warnings land here</span>
              <input data-testid="settings-alert-email" type="email" value={settings.alert_email || ""}
                onChange={(e) => setSettings({ ...settings, alert_email: e.target.value })}
                placeholder="you@gmail.com"
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep placeholder:text-clay/50 focus:outline-none focus:border-gold" />
            </label>
            <p className="text-xs text-clay/80">Use an inbox you actually read (your Gmail works perfectly). Alerts fire when any size drops below 3 pieces or sells out.</p>

            <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark border-b border-gold/25 pb-2 pt-4">Announcement</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="settings-announcement-toggle" type="checkbox" checked={settings.announcement_enabled}
                onChange={(e) => setSettings({ ...settings, announcement_enabled: e.target.checked })} className="accent-[#5C1A1B] w-4 h-4" />
              <span className="text-sm text-maroon-deep">Show announcement strip across the store</span>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Announcement Text</span>
              <input data-testid="settings-announcement-text" value={settings.announcement}
                onChange={(e) => setSettings({ ...settings, announcement: e.target.value })}
                placeholder="The Debut Collection is live — complimentary shipping all week"
                className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3 text-sm text-maroon-deep placeholder:text-clay/50 focus:outline-none focus:border-gold" />
            </label>
          </div>

          <button
            data-testid="settings-save-button"
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="mt-8 w-full bg-maroon text-ivory py-4 text-xs tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors disabled:opacity-60"
          >
            {savingSettings ? "Saving…" : "Save Settings"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
